import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput, materialNormalOutput, materialVectorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"
import type {FloatOutput} from "#material/cycles/types"

export interface TextureSetSockets extends MaterialNodeSockets {
    inputs: {
        normalStrength: "float" // found in inputs: color,point; found in settings: scalar,float; inferred from data: float
        uv: "vector" // found in inputs: point,vector; found in settings: input; inferred from data: null
    }
    settings: {
        normalStrength: "float" // found in inputs: color,point; found in settings: scalar,float; inferred from data: float
        normalRotation: "float"
        textureSetRevisionId: "string" // found in inputs: -; found in settings: string; inferred from data: string,undefined
        transmissionMax: "float" // found in inputs: -; found in settings: scalar; inferred from data: float
        transmissionMin: "float" // found in inputs: -; found in settings: scalar; inferred from data: float
        uv: "vector" // found in inputs: point,vector; found in settings: input; inferred from data: null
    }
    outputs: {
        baseColor: "color"
        metallic: "float"
        specular: "float"
        roughness: "float"
        anisotropic: "float"
        anisotropicRotation: "float"
        alpha: "float"
        normal: "normal"
        displacement: "vector"
        transmission: "color"
    }
}

export const textureSetDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.CmTextureSet,
    },
    nodeClass: "TextureSet",
    hierarchical: true,
    ui: {
        label: "Texture Set",
        color: "#9e6034",
        items: [
            {name: "baseColor", valueType: "color", label: "Base Color", output: true},
            {name: "metallic", valueType: "float", label: "Metallic", output: true},
            {name: "specular", valueType: "float", label: "Specular", output: true},
            {name: "roughness", valueType: "float", label: "Roughness", output: true},
            {name: "anisotropic", valueType: "float", label: "Anisotropic", output: true},
            {name: "anisotropicRotation", valueType: "float", label: "Anisotropy Rotation", output: true},
            {name: "alpha", valueType: "float", label: "Alpha", output: true},
            {name: "normal", valueType: "normal", label: "Normal", output: true},
            {name: "displacement", valueType: "vector", label: "Displacement", output: true},
            {name: "transmission", valueType: "color", label: "Transmission", output: true},
            {custom: true, componentClass: "imageTextureSet"},
            {name: "uv", valueType: "vector", label: "UV", input: true, setting: true},
            {name: "normalStrength", valueType: "float", label: "Normal Strength", defaultValue: 1, input: true, setting: true},
        ],
        width: 175,
        support: {
            cycles: true,
            three: {full: false, remark: "Some filters/extension modes unsupported"},
        },
    },
    validation: {
        returns: z.strictObject({
            baseColor: materialColorOutput,
            metallic: z.union([materialFloatOutput, materialColorOutput]) as z.ZodMiniType<FloatOutput>,
            specular: z.union([materialFloatOutput, materialColorOutput]) as z.ZodMiniType<FloatOutput>,
            roughness: z.union([materialFloatOutput, materialColorOutput]) as z.ZodMiniType<FloatOutput>,
            anisotropic: materialFloatOutput,
            anisotropicRotation: z.union([materialFloatOutput, materialColorOutput]) as z.ZodMiniType<FloatOutput>,
            alpha: z.union([materialFloatOutput, materialColorOutput]) as z.ZodMiniType<FloatOutput>,
            normal: materialNormalOutput,
            displacement: materialVectorOutput,
            transmission: materialColorOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({uv: optionalMaterialInput, normalStrength: optionalMaterialInput}),
            settings: z.strictObject({
                normalRotation: z.optional(z.number()),
                normalStrength: z.optional(z.number()),
                textureSetRevisionId: z.optional(z.string()),
                transmissionMax: z.optional(z.number()),
                transmissionMin: z.optional(z.number()),
                uv: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TextureSetSockets>
